import React, { useEffect, useState } from 'react';
import { landlordAPI } from '../../lib/api';
import { formatCurrency, downloadBlob } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Loader2, BookOpen, TrendingUp, TrendingDown, Minus, FileSpreadsheet, FileText } from 'lucide-react';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatMonth(ym) {
  const [y, m] = ym.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
}

export default function LandlordBookkeeping() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState('6');
  const [exporting, setExporting] = useState(false);

  useEffect(() => { loadData(); }, [months]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional mount-only load

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await landlordAPI.getBookkeeping(parseInt(months));
      setData(res.data);
    } catch (error) {
      console.error('Error loading bookkeeping:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await landlordAPI.exportBookkeeping(format, parseInt(months));
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      downloadBlob(response.data, `bookkeeping.${ext}`);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { ledger = [], summary = {} } = data || {};

  return (
    <div className="space-y-6" data-testid="landlord-bookkeeping-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold">Bookkeeping</h1>
          <p className="text-muted-foreground mt-1">Monthly income vs expenses ledger</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => handleExport('excel')} disabled={exporting} data-testid="bookkeeping-export-excel">
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            {exporting ? 'Exporting...' : 'Excel'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} disabled={exporting} data-testid="bookkeeping-export-pdf">
            <FileText className="w-4 h-4 mr-1" />
            {exporting ? 'Exporting...' : 'PDF'}
          </Button>
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger className="w-[160px]" data-testid="bookkeeping-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">This month</SelectItem>
              <SelectItem value="3">Last 3 months</SelectItem>
              <SelectItem value="6">Last 6 months</SelectItem>
              <SelectItem value="12">Last 12 months</SelectItem>
              <SelectItem value="24">Last 24 months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Income</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(summary.total_income || 0)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Expenses</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.total_expenses || 0)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Net Profit</p>
                <p className={`text-2xl font-bold ${(summary.net_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(summary.net_profit || 0)}
                </p>
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${(summary.net_profit || 0) >= 0 ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                {(summary.net_profit || 0) >= 0 ? <TrendingUp className="w-5 h-5 text-emerald-600" /> : <TrendingDown className="w-5 h-5 text-red-600" />}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ledger Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Monthly Ledger
          </CardTitle>
          <CardDescription>Income, expenses, and running balance by month</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {ledger.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No financial data found for this period.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-4 py-3 font-semibold">Month</th>
                    <th className="text-right px-4 py-3 font-semibold">Income</th>
                    <th className="text-center px-3 py-3 font-semibold">Transactions</th>
                    <th className="text-right px-4 py-3 font-semibold">Expenses</th>
                    <th className="text-center px-3 py-3 font-semibold">Transactions</th>
                    <th className="text-right px-4 py-3 font-semibold">Net</th>
                    <th className="text-right px-4 py-3 font-semibold">Running Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.map((row) => (
                    <tr key={row.month} className="border-b border-border/50 hover:bg-muted/20">
                      <td className="px-4 py-3 font-medium">{formatMonth(row.month)}</td>
                      <td className="text-right px-4 py-3 text-emerald-600 font-semibold tabular-nums">{formatCurrency(row.income)}</td>
                      <td className="text-center px-3 py-3 text-muted-foreground">{row.income_count}</td>
                      <td className="text-right px-4 py-3 text-red-600 font-semibold tabular-nums">{formatCurrency(row.expenses)}</td>
                      <td className="text-center px-3 py-3 text-muted-foreground">{row.expense_count}</td>
                      <td className="text-right px-4 py-3">
                        <span className={`inline-flex items-center gap-1 font-semibold tabular-nums ${row.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {row.net > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : row.net < 0 ? <TrendingDown className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                          {formatCurrency(Math.abs(row.net))}
                        </span>
                      </td>
                      <td className="text-right px-4 py-3 font-semibold tabular-nums">
                        <span className={row.running_balance >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                          {formatCurrency(row.running_balance)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-border bg-muted/50 font-semibold">
                    <td className="px-4 py-3">Total</td>
                    <td className="text-right px-4 py-3 text-emerald-600 tabular-nums">{formatCurrency(summary.total_income || 0)}</td>
                    <td></td>
                    <td className="text-right px-4 py-3 text-red-600 tabular-nums">{formatCurrency(summary.total_expenses || 0)}</td>
                    <td></td>
                    <td className="text-right px-4 py-3">
                      <span className={`tabular-nums ${(summary.net_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(summary.net_profit || 0)}
                      </span>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
